/**
 * @file dpi.ts
 * @description Telink 3228C 维盛 AP 鼠标已取证的七档 DPI 功能实现。
 */

import { DeviceError, DeviceErrorCode } from '../../../../errors/DeviceError'
import { DPIFeature } from '../../../../features/dpi/contract'
import type { BaseDevice } from '../../../../runtime/BaseDevice'
import type { DPIDirect, GearType } from '../../../../types/dpi'
import type { mouseUserCustom } from '../../../../types/config'
import type { VisionApFunctionAreaSession } from '../../protocols/vision-ap-mouse'
import { assertVisionApProfileIndex } from '../../protocols/vision-ap-mouse'

const DPI_GEAR_COUNT = 7
const DPI_GEAR_BASE_OFFSET = 14
const DPI_GEAR_BYTE_LENGTH = 9
const CURRENT_DPI_OFFSET = 12

interface TelinkApMouseDpiDevice extends BaseDevice {
  runFunctionAreaSession<T>(
    operation: (session: VisionApFunctionAreaSession) => Promise<T>,
    profileIndex?: number,
  ): Promise<T>
  updateFunctionArea(update: (area: Uint8Array) => void, profileIndex?: number): Promise<void>
}

/** 维盛 AP 鼠标 DPI feature；在 SDK 内将页面的可见档位索引转换为物理槽位。 */
export class Telink3228CMouseDPI extends DPIFeature {
  private readonly apDevice: TelinkApMouseDpiDevice
  // 页面新增的 UUID 无法直接编码物理槽位；在同一设备实例内保持其分配结果，避免后续编辑或删除时槽位漂移。
  private readonly clientGearSlots = new Map<string, number>()

  constructor(device: TelinkApMouseDpiDevice) {
    super(device)
    this.apDevice = device
  }

  public async getGears(configIndex = 0): Promise<GearType[]> {
    this.assertConfigIndex(configIndex)
    return this.apDevice.runFunctionAreaSession(async (session) => {
      const area = await session.readFunctionAreaSnapshot()
      return this.parseGears(area, this.getEnabledSlots(area))
    }, configIndex)
  }

  public async getCurrentGear(configIndex = 0): Promise<number> {
    this.assertConfigIndex(configIndex)
    return this.apDevice.runFunctionAreaSession(async (session) => {
      const area = await session.readFunctionAreaSnapshot()
      return this.parseCurrentGearSnapshot(area).cpiLev
    }, configIndex)
  }

  /** 从同一份功能区快照解析当前可见档位与 DPI，供主动同步事件原子刷新页面状态。 */
  public async getCurrentGearSnapshot(
    configIndex = 0,
  ): Promise<Pick<mouseUserCustom, 'cpiLev' | 'xCpiVal' | 'yCpiVal'>> {
    this.assertConfigIndex(configIndex)
    return this.apDevice.runFunctionAreaSession(async (session) => {
      const area = await session.readFunctionAreaSnapshot()
      return this.parseCurrentGearSnapshot(area)
    }, configIndex)
  }

  public async getEnableGear(configIndex = 0): Promise<number> {
    this.assertConfigIndex(configIndex)
    const gears = await this.getGears(configIndex)
    return gears.length
  }

  public async setDeviceGear(value: number, configIndex = 0): Promise<void> {
    this.assertConfigIndex(configIndex)
    this.assertGearIndex(value, '目标 DPI 档位')

    await this.apDevice.updateFunctionArea((area) => {
      const slot = this.getSlotForVisibleIndex(area, value, '目标 DPI 档位')

      area[CURRENT_DPI_OFFSET] = slot
    }, configIndex)
  }

  public async setDeviceDPI(
    index: number,
    value: number,
    direct: DPIDirect,
    configIndex = 0,
  ): Promise<void> {
    this.assertConfigIndex(configIndex)
    this.assertGearIndex(index, '目标 DPI 档位')
    this.assertDpiValue(value)

    await this.apDevice.updateFunctionArea((area) => {
      const slot = this.getSlotForVisibleIndex(area, index, '目标 DPI 档位')
      const offset = this.getGearOffset(slot)

      this.writeDpiValue(area, offset + (direct === 'x' ? 2 : 4), value)
    }, configIndex)
  }

  public async setDeviceGears(gears: GearType[], configIndex = 0): Promise<void> {
    this.assertConfigIndex(configIndex)
    this.assertGearCollection(gears)

    let assignments: Array<{ gear: GearType; slot: number }> = []
    await this.apDevice.updateFunctionArea((area) => {
      assignments = this.assignGearsToSlots(gears)
      const assignedSlots = new Set(assignments.map(({ slot }) => slot))

      for (const { gear, slot } of assignments) {
        this.writeGear(area, slot, gear)
      }

      for (let slot = 0; slot < DPI_GEAR_COUNT; slot += 1) {
        if (!assignedSlots.has(slot)) {
          const offset = this.getGearOffset(slot)
          area[offset] = 0
        }
      }

      const enabledSlots = this.getEnabledSlots(area)
      if (!this.isSlotEnabled(area, area[CURRENT_DPI_OFFSET])) {
        area[CURRENT_DPI_OFFSET] = enabledSlots[0]
      }
    }, configIndex)
    this.updateClientGearSlots(gears, assignments)
  }

  private parseGears(area: Uint8Array, slots: number[]): GearType[] {
    return slots.map((slot) => {
      const offset = this.getGearOffset(slot)
      const x = this.readDpiValue(area, offset + 2)
      const y = this.readDpiValue(area, offset + 4)
      return {
        id: `telink-ap-dpi-${slot}`,
        x,
        y,
        // 与其他 VT 系列保持一致：由 X/Y DPI 是否相等判断是否独立，不使用 dpi_xy。
        independentXY: x !== y,
        enable: area[offset] === 1,
      }
    })
  }

  /** 从已读取的功能区快照解析当前 DPI，供设备主动同步复用同一次 A4 读取。 */
  public parseCurrentGearSnapshot(
    area: Uint8Array,
  ): Pick<mouseUserCustom, 'cpiLev' | 'xCpiVal' | 'yCpiVal'> {
    const rawIndex = area[CURRENT_DPI_OFFSET]
    this.assertGearIndex(rawIndex, '设备当前 DPI 档位')
    const enabledSlots = this.getEnabledSlots(area)
    const visibleIndex = enabledSlots.indexOf(rawIndex)
    // 接收器与鼠标都可能返回已关闭的历史槽位；回退到首个有效槽位。
    const effectiveSlot = visibleIndex === -1 ? enabledSlots[0] : rawIndex
    const offset = this.getGearOffset(effectiveSlot)
    return {
      cpiLev: (visibleIndex === -1 ? 0 : visibleIndex) as mouseUserCustom['cpiLev'],
      xCpiVal: this.readDpiValue(area, offset + 2),
      yCpiVal: this.readDpiValue(area, offset + 4),
    }
  }

  private getEnabledSlots(area: Uint8Array): number[] {
    const slots = Array.from({ length: DPI_GEAR_COUNT }, (_, slot) => slot).filter(
      (slot) => area[this.getGearOffset(slot)] === 1,
    )
    if (slots.length === 0) {
      throw new DeviceError(DeviceErrorCode.READ_FAILED, '设备未启用任何 DPI 档位')
    }
    return slots
  }

  private isSlotEnabled(area: Uint8Array, slot: number): boolean {
    return area[this.getGearOffset(slot)] === 1
  }

  private getSlotForVisibleIndex(area: Uint8Array, index: number, field: string): number {
    const slot = this.getEnabledSlots(area)[index]
    if (slot === undefined) {
      throw new DeviceError(DeviceErrorCode.INVALID_PARAM, `${field}未启用`)
    }
    return slot
  }

  private assignGearsToSlots(gears: GearType[]): Array<{ gear: GearType; slot: number }> {
    const assignments: Array<{ gear: GearType; slot: number }> = []
    const assignedSlots = new Set<number>()
    const newGears: GearType[] = []

    for (const gear of gears) {
      const slot = this.getSlotFromGearId(gear.id)
      if (slot === undefined) {
        newGears.push(gear)
        continue
      }
      if (assignedSlots.has(slot)) {
        throw new DeviceError(DeviceErrorCode.INVALID_PARAM, `DPI 档位 ${slot + 1} 被重复提交`)
      }
      assignedSlots.add(slot)
      assignments.push({ gear, slot })
    }

    const availableSlots = Array.from({ length: DPI_GEAR_COUNT }, (_, slot) => slot).filter(
      (slot) => !assignedSlots.has(slot),
    )
    if (newGears.length > availableSlots.length) {
      throw new DeviceError(DeviceErrorCode.MAX_LIMIT_REACHED, 'DPI 档位数量超过设备支持的 7 档')
    }

    newGears.forEach((gear, index) => {
      assignments.push({ gear, slot: availableSlots[index] })
    })
    return assignments
  }

  private updateClientGearSlots(
    gears: GearType[],
    assignments: Array<{ gear: GearType; slot: number }>,
  ): void {
    const clientGearIds = new Set(
      gears
        .filter((gear) => this.getPhysicalSlotFromGearId(gear.id) === undefined)
        .map((gear) => gear.id),
    )
    this.clientGearSlots.clear()
    for (const { gear, slot } of assignments) {
      if (clientGearIds.has(gear.id)) {
        this.clientGearSlots.set(gear.id, slot)
      }
    }
  }

  private writeGear(area: Uint8Array, slot: number, gear: GearType): void {
    const offset = this.getGearOffset(slot)
    // dpi_lock 仍用于档位增删；dpi_xy 保持设备原值，XY 状态由两组 DPI 值判断。
    area[offset] = gear.enable ? 1 : 0
    this.writeDpiValue(area, offset + 2, gear.x)
    this.writeDpiValue(area, offset + 4, gear.y)
  }

  private getGearOffset(slot: number): number {
    return DPI_GEAR_BASE_OFFSET + slot * DPI_GEAR_BYTE_LENGTH
  }

  private readDpiValue(area: Uint8Array, offset: number): number {
    return area[offset] + (area[offset + 1] << 8) + 1
  }

  private writeDpiValue(area: Uint8Array, offset: number, value: number): void {
    const rawValue = value - 1
    area[offset] = rawValue & 0xff
    area[offset + 1] = (rawValue >> 8) & 0xff
  }

  private assertConfigIndex(configIndex: number): void {
    assertVisionApProfileIndex(configIndex)
  }

  private assertGearIndex(index: number, field: string): void {
    if (!Number.isInteger(index) || index < 0 || index >= DPI_GEAR_COUNT) {
      throw new DeviceError(DeviceErrorCode.INVALID_PARAM, `${field}超出 0-6 范围`)
    }
  }

  private assertGearCollection(gears: GearType[]): void {
    if (!Array.isArray(gears) || gears.length === 0) {
      throw new DeviceError(DeviceErrorCode.INVALID_PARAM, '至少需要保留一个 DPI 档位')
    }
    if (gears.length > DPI_GEAR_COUNT) {
      throw new DeviceError(DeviceErrorCode.MAX_LIMIT_REACHED, 'DPI 档位数量超过设备支持的 7 档')
    }

    for (const gear of gears) {
      if (!gear || typeof gear.id !== 'string' || gear.id.length === 0) {
        throw new DeviceError(DeviceErrorCode.INVALID_PARAM, 'DPI 档位必须包含有效 ID')
      }
      if (typeof gear.enable !== 'boolean' || typeof gear.independentXY !== 'boolean') {
        throw new DeviceError(DeviceErrorCode.INVALID_PARAM, 'DPI 档位开关必须为布尔值')
      }
      this.assertDpiValue(gear.x)
      this.assertDpiValue(gear.y)
    }

    if (!gears.some((gear) => gear.enable)) {
      throw new DeviceError(DeviceErrorCode.INVALID_PARAM, '至少需要启用一个 DPI 档位')
    }
  }

  private getSlotFromGearId(id: string): number | undefined {
    const clientSlot = this.clientGearSlots.get(id)
    if (clientSlot !== undefined) return clientSlot
    return this.getPhysicalSlotFromGearId(id)
  }

  private getPhysicalSlotFromGearId(id: string): number | undefined {
    const match = /^telink-ap-dpi-([0-6])$/.exec(id)
    return match ? Number(match[1]) : undefined
  }

  private assertDpiValue(value: number): void {
    if (!Number.isInteger(value) || value < 1 || value > 0x10000) {
      throw new DeviceError(DeviceErrorCode.INVALID_PARAM, 'DPI 必须为 1-65536 的整数')
    }
  }
}
